import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { addDoc, collection, serverTimestamp } from "firebase/firestore";
import { db } from "./firebase";

const backdrop = {
  hidden: { opacity: 0 },
  show: { opacity: 1, transition: { duration: 0.2 } },
  exit: { opacity: 0, transition: { duration: 0.18 } },
};

const panel = {
  hidden: { opacity: 0, y: 18, scale: 0.985, filter: "blur(8px)" },
  show: {
    opacity: 1,
    y: 0,
    scale: 1,
    filter: "blur(0px)",
    transition: { duration: 0.35, ease: "easeOut" },
  },
  exit: {
    opacity: 0,
    y: 10,
    scale: 0.985,
    filter: "blur(8px)",
    transition: { duration: 0.22, ease: "easeIn" },
  },
};

function Field({ label, required, children }) {
  return (
    <div className="space-y-1.5">
      <label className="block text-[11px] uppercase tracking-[0.16em] text-white/60">
        {label} {required ? <span className="text-[#E53935]">*</span> : null}
      </label>
      {children}
    </div>
  );
}

const Input = React.forwardRef(function Input(props, ref) {
  return (
    <input
      ref={ref}
      {...props}
      className="w-full rounded-2xl border border-white/10 bg-white/5 px-3.5 py-2.5 text-sm text-white placeholder:text-white/40 focus:outline-none focus:ring-2 focus:ring-[#E53935]/60 focus:border-[#E53935]/40 transition"
    />
  );
});

function Select({ value, onChange, options = [] }) {
  return (
    <select
      value={value}
      onChange={onChange}
      className="w-full rounded-2xl border border-white/10 bg-white/5 px-3.5 py-2.5 text-sm text-white focus:outline-none focus:ring-2 focus:ring-[#E53935]/60 focus:border-[#E53935]/40 transition"
    >
      {options.map((o) => (
        <option key={o} value={o} className="bg-[#0B1020]">
          {o}
        </option>
      ))}
    </select>
  );
}

function parsePlaceToAddress(place) {
  const comps = place?.address_components || [];
  const get = (type) => comps.find((c) => c.types?.includes(type))?.long_name || "";
  const getShort = (type) => comps.find((c) => c.types?.includes(type))?.short_name || "";

  const streetNumber = get("street_number");
  const route = get("route");
  const street = [streetNumber, route].filter(Boolean).join(" ").trim();

  const city =
    get("locality") ||
    get("sublocality") ||
    get("postal_town") ||
    get("administrative_area_level_2");

  const state = getShort("administrative_area_level_1");
  const zip = get("postal_code");

  const lat = typeof place?.geometry?.location?.lat === "function"
    ? place.geometry.location.lat()
    : null;

  const lng = typeof place?.geometry?.location?.lng === "function"
    ? place.geometry.location.lng()
    : null;

  return { street, city, state, zip, lat, lng };
}

function AddressAutocomplete({ value, onPick, onChange, active }) {
  const inputRef = useRef(null);
  const acRef = useRef(null);

  useEffect(() => {
    if (!active) return;
    if (!window.google?.maps?.places || !inputRef.current) return;
    if (acRef.current) return;

    const ac = new window.google.maps.places.Autocomplete(inputRef.current, {
      types: ["address"],
      componentRestrictions: { country: "us" },
    });

    if (ac.setFields) {
      ac.setFields([
        "address_components",
        "formatted_address",
        "geometry",
        "name",
      ]);
    }

    const listener = ac.addListener("place_changed", () => {
      const place = ac.getPlace();
      const formatted = place?.formatted_address || inputRef.current.value;
      onPick(place, formatted);
    });

    acRef.current = { ac, listener };

    return () => {
      try {
        listener?.remove?.();
        window.google?.maps?.event?.clearInstanceListeners?.(ac);
      } catch {}
      acRef.current = null;
    };
  }, [active, onPick]);

  return (
    <Input
      ref={inputRef}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder="Search address"
      autoComplete="off"
    />
  );
}

function MapPreview({ lat, lng }) {
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const markerRef = useRef(null);

  useEffect(() => {
    if (!window.google?.maps || !mapRef.current || lat == null || lng == null) return;

    const center = { lat, lng };

    if (!mapInstanceRef.current) {
      mapInstanceRef.current = new window.google.maps.Map(mapRef.current, {
        center,
        zoom: 15,
        disableDefaultUI: true,
        zoomControl: true,
        styles: [
          { elementType: "geometry", stylers: [{ color: "#111827" }] },
          { elementType: "labels.text.fill", stylers: [{ color: "#d1d5db" }] },
          { elementType: "labels.text.stroke", stylers: [{ color: "#111827" }] },
          { featureType: "road", elementType: "geometry", stylers: [{ color: "#374151" }] },
          { featureType: "water", elementType: "geometry", stylers: [{ color: "#0f172a" }] },
          { featureType: "poi", elementType: "labels", stylers: [{ visibility: "off" }] },
        ],
      });
    } else {
      mapInstanceRef.current.setCenter(center);
    }

    if (!markerRef.current) {
      markerRef.current = new window.google.maps.Marker({
        position: center,
        map: mapInstanceRef.current,
      });
    } else {
      markerRef.current.setPosition(center);
    }
  }, [lat, lng]);

  if (lat == null || lng == null) return null;

  return (
    <div className="space-y-2">
      <p className="text-[11px] uppercase tracking-[0.16em] text-white/50">
        Property Preview
      </p>
      <div
        ref={mapRef}
        className="h-52 w-full overflow-hidden rounded-2xl border border-white/10"
      />
    </div>
  );
}

export function EstimateModal({ open, onClose }) {
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");

  const SERVICE_TYPES = useMemo(
    () => [
      "General Home Service",
      "Construction / Structural",
      "Exterior / Outdoor",
      "Interior Remodeling",
      "Roofing",
      "HVAC",
      "Electrical",
      "Plumbing",
      "Commercial",
      "Gate Repair",
      "Handyman",
      "Other",
    ],
    []
  );

  const initial = useMemo(
    () => ({
      firstName: "",
      lastName: "",
      phone: "",
      email: "",
      addressSearch: "",
      street: "",
      city: "",
      state: "",
      zip: "",
      serviceType: "General Home Service",
      notes: "",
      consent: false,
      lat: null,
      lng: null,
    }),
    []
  );

  const [form, setForm] = useState(initial);

  const set = (k) => (e) => {
    const v = e.target.type === "checkbox" ? e.target.checked : e.target.value;
    setForm((p) => ({ ...p, [k]: v }));
  };

  const close = () => {
    if (loading) return;
    setSent(false);
    setError("");
    setForm(initial);
    onClose?.();
  };

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => e.key === "Escape" && close();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, loading]);

  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const styleId = "pac-zfix";
    if (!document.getElementById(styleId)) {
      const s = document.createElement("style");
      s.id = styleId;
      s.innerHTML = `.pac-container { z-index: 2147483647 !important; }`;
      document.head.appendChild(s);
    }
  }, [open]);

  const onPickPlace = useCallback((place, formatted) => {
    const parsed = parsePlaceToAddress(place);
    setForm((p) => ({
      ...p,
      addressSearch: formatted || p.addressSearch,
      street: parsed.street || p.street,
      city: parsed.city || p.city,
      state: parsed.state || p.state,
      zip: parsed.zip || p.zip,
      lat: parsed.lat,
      lng: parsed.lng,
    }));
  }, []);

const submit = async () => {
  if (loading) return;

  setError("");

  if (
    form.firstName.trim().length < 2 ||
    form.lastName.trim().length < 2 ||
    form.phone.trim().length < 7 ||
    !form.email.includes("@") ||
    form.street.trim().length < 4 ||
    form.city.trim().length < 2 ||
    form.state.trim().length < 2 ||
    form.zip.trim().length < 5 ||
    !form.consent
  ) {
    setError("Please complete all required fields.");
    return;
  }

  try {
    setLoading(true);

    const payload = {
      firstName: form.firstName.trim(),
      lastName: form.lastName.trim(),
      fullName: `${form.firstName} ${form.lastName}`.trim(),
      phone: form.phone.trim(),
      email: form.email.trim(),
      addressSearch: form.addressSearch.trim(),
      street: form.street.trim(),
      city: form.city.trim(),
      state: form.state.trim(),
      zip: form.zip.trim(),
      address: `${form.street}, ${form.city}, ${form.state} ${form.zip}`.trim(),
      serviceType: form.serviceType,
      notes: form.notes.trim(),
      consent: form.consent,
      lat: form.lat,
      lng: form.lng,
      source: "htx-home-solutions-site",
      createdAt: serverTimestamp(),
    };

    const submitPromise = addDoc(collection(db, "estimates"), payload);

    const timeoutPromise = new Promise((_, reject) =>
      setTimeout(() => reject(new Error("Request timed out. Please try again.")), 12000)
    );

    await Promise.race([submitPromise, timeoutPromise]);

    setSent(true);
  } catch (err) {
    console.error("Estimate submit failed:", err);
    setError(err?.message || "Something went wrong submitting your request. Please try again.");
  } finally {
    setLoading(false);
  }
};

  return (
    <AnimatePresence>
      {open ? (
        <motion.div
          className="fixed inset-0 z-[100] flex items-center justify-center overflow-y-auto px-4 py-6"
          initial="hidden"
          animate="show"
          exit="exit"
          variants={backdrop}
        >
          <div className="absolute inset-0 bg-black/80 backdrop-blur-md" onClick={close} />

          <motion.div
  variants={panel}
  className="relative z-10 w-full max-w-2xl max-h-[90vh] overflow-y-auto rounded-3xl border border-white/10 bg-[#0B1020] p-6 shadow-[0_30px_120px_rgba(0,0,0,0.75)]"
  onClick={(e) => e.stopPropagation()}
>
            {sent ? (
  <div className="py-6 text-center">
    <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full border border-[#E53935]/30 bg-[#E53935]/20">
      <span className="text-2xl text-white">✓</span>
    </div>
    <h3 className="text-xl font-semibold text-white">Thank you!</h3>
    <p className="mt-3 text-sm leading-relaxed text-white/70">
      Your estimate request has been submitted successfully. A member of our team
      will review the details and reach out soon.
    </p>
    <button
      onClick={close}
      className="mt-6 rounded-full bg-[#E53935] px-6 py-3 text-sm font-semibold text-white"
    >
      Close
    </button>
  </div>
) : (
              <>
                <h3 className="text-lg font-semibold text-white">Request a Free Estimate</h3>
                <p className="mt-2 text-sm text-white/60">
                  Tell us what you need and where the property is.
                </p>

                <form
  className="mt-5 space-y-4"
  onSubmit={(e) => {
    e.preventDefault();
    submit();
  }}
>
                  <div className="grid gap-4 sm:grid-cols-2">
                    <Field label="First Name" required>
                      <Input value={form.firstName} onChange={set("firstName")} />
                    </Field>
                    <Field label="Last Name" required>
                      <Input value={form.lastName} onChange={set("lastName")} />
                    </Field>
                  </div>

                  <div className="grid gap-4 sm:grid-cols-2">
                    <Field label="Phone" required>
                      <Input value={form.phone} onChange={set("phone")} />
                    </Field>
                    <Field label="Email" required>
                      <Input value={form.email} onChange={set("email")} type="email" />
                    </Field>
                  </div>

                  <Field label="Search Address" required>
                    <AddressAutocomplete
                      value={form.addressSearch}
                      onChange={(v) => setForm((p) => ({ ...p, addressSearch: v }))}
                      onPick={onPickPlace}
                      active={open}
                    />
                  </Field>

                  {(form.street || form.city || form.state || form.zip) && (
                    <div className="grid gap-4 sm:grid-cols-3">
                      <Field label="City" required>
                        <Input value={form.city} onChange={set("city")} />
                      </Field>
                      <Field label="State" required>
                        <Input value={form.state} onChange={set("state")} />
                      </Field>
                      <Field label="Zip" required>
                        <Input value={form.zip} onChange={set("zip")} />
                      </Field>
                    </div>
                  )}

                  <MapPreview lat={form.lat} lng={form.lng} />

                  <Field label="Service Type" required>
                    <Select
                      value={form.serviceType}
                      onChange={set("serviceType")}
                      options={SERVICE_TYPES}
                    />
                  </Field>

                  <Field label="Project Details">
                    <textarea
                      rows={4}
                      value={form.notes}
                      onChange={set("notes")}
                      className="w-full rounded-2xl border border-white/10 bg-white/5 px-3.5 py-2.5 text-sm text-white placeholder:text-white/40 focus:outline-none focus:ring-2 focus:ring-[#E53935]/60"
                    />
                  </Field>

                  <label className="flex items-start gap-3 text-xs text-white/70">
                    <input
                      type="checkbox"
                      checked={form.consent}
                      onChange={set("consent")}
                      className="mt-0.5"
                    />
                    I agree to be contacted about my estimate request.
                  </label>

                  {error ? <p className="text-sm text-red-400">{error}</p> : null}

                  <button
  type="submit"
  disabled={loading}
  className="w-full rounded-full bg-[#E53935] px-6 py-3 text-sm font-semibold text-white shadow-[0_18px_45px_rgba(229,57,53,0.35)] disabled:opacity-70"
>
  {loading ? "Submitting..." : "Submit Request →"}
</button>
                </form>
              </>
            )}
          </motion.div>
        </motion.div>
      ) : null}
    </AnimatePresence>
  );
}